import { memo, useMemo, useState } from 'react';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { ActionIcon, TextInput } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useController } from 'react-hook-form';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import {
	EditingRateContainer,
	EditingRateContent,
	EditingRateValue,
	RateActions,
	RateIndicator,
	StaticRateContainer,
	StaticRateContent,
	StaticRateValue,
} from './styled';
import { StatusDot } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/FXRatesDrawer/styled';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';

interface Props {
	currency: CurrencyType;
	isEditable?: boolean;
}

const EditableRateSection = ({ currency, isEditable = true }: Props) => {
	const [isEditing, setIsEditing] = useState(false);

	const customFieldName = `rates.${currency}.custom`;
	const historyFieldName = `rates.${currency}.history`;

	const {
		field: { value: customFieldValue, onChange: customFieldValueOnChange },
		fieldState: { invalid: isInvalid },
	} = useController({ name: customFieldName });
	const {
		field: { value: historyFieldValue },
	} = useController({ name: historyFieldName });
	const tValuations = useTranslationValuations();
	const notApplicableValue = tValuations('fx_drawer.not_applicable_value');

	const finalValuationRate = useMemo(() => {
		const numberToDisplay = customFieldValue ? Number(customFieldValue) : historyFieldValue;
		if (!numberToDisplay) {
			return notApplicableValue;
		}

		return formatNumber({
			value: numberToDisplay,
			maxPrecision: 5,
			minPrecision: 0,
			lockPrecision: false,
		});
	}, [customFieldValue, historyFieldValue, notApplicableValue]);

	const [previousCustomValue, setPreviousCustomValue] = useState(customFieldValue);

	const isModified = !!customFieldValue;

	const updateCustomValue = (value: string | null) => {
		customFieldValueOnChange(value);
	};

	const onEditStart = () => {
		setIsEditing(true);
		setPreviousCustomValue(customFieldValue);
		if (!customFieldValue) {
			updateCustomValue(historyFieldValue?.toFixed(5) ?? '1');
		}
	};

	const onEditCancel = () => {
		setIsEditing(false);
		updateCustomValue(previousCustomValue);
	};

	const onReset = () => {
		setPreviousCustomValue(undefined);
		updateCustomValue(null);
	};

	const onSave = () => {
		setIsEditing(false);
	};

	const Container = isEditing ? EditingRateContainer : StaticRateContainer;
	const Content = isEditing ? EditingRateContent : StaticRateContent;
	const Value = isEditing ? EditingRateValue : StaticRateValue;

	return (
		<Container>
			<Content>
				<RateIndicator>
					{!isEditing ? (
						<>
							<StatusDot $status={isModified ? 'modified' : 'valuation'} />
							<TextV2 variant="sans/14/400">
								{isModified
									? tValuations('fx_drawer.modified_rate_title')
									: tValuations('fx_drawer.valuation_rate_title')}
							</TextV2>
						</>
					) : (
						<TextV2 variant="sans/09/400">
							{tValuations('fx_drawer.modified_rate_title')}
						</TextV2>
					)}
				</RateIndicator>
				<Value>
					{isEditing ? (
						<TextInput
							data-testid={
								VALUATION_ELEMENT_ID.FX_EXCHANGE_RATE_AT_VALUATION_EDIT_INPUT
							}
							size="xs"
							error={isInvalid}
							value={customFieldValue ?? historyFieldValue?.toFixed(5) ?? '1'}
							onChange={(e) => {
								updateCustomValue(e.target.value);
							}}
						/>
					) : (
						<TextV2
							data-testid={
								VALUATION_ELEMENT_ID.FX_EXCHANGE_LIVE_RATE_AT_VALUATION_DATE
							}
							variant="sans/14/400"
						>
							{finalValuationRate}
						</TextV2>
					)}
				</Value>
			</Content>
			<RateActions>
				{!isEditing ? (
					<>
						{isModified && (
							<ActionIcon
								variant="subtle"
								size="sm"
								onClick={onReset}
								data-testid={
									VALUATION_ELEMENT_ID.FX_EXCHANGE_RATE_AT_VALUATION_RESET
								}
								sx={(theme) => ({ color: theme.colors.dark[8] })}
							>
								<Icon variant="refresh-v3" size="lg" />
							</ActionIcon>
						)}
						{isEditable && (
							<ActionIcon
								variant="subtle"
								size="sm"
								data-testid={
									VALUATION_ELEMENT_ID.FX_EXCHANGE_RATE_AT_VALUATION_EDIT
								}
								onClick={onEditStart}
								sx={(theme) => ({ color: theme.colors.dark[8] })}
							>
								<Icon variant="pencil-edit-line" size="lg" />
							</ActionIcon>
						)}
					</>
				) : (
					<>
						<ActionIcon
							variant="filled"
							size="sm"
							data-testid={VALUATION_ELEMENT_ID.FX_EXCHANGE_RATE_AT_VALUATION_CANCEL}
							onClick={onEditCancel}
							sx={(theme) => ({
								color: theme.colors.black[0],
								backgroundColor: theme.colors.gray[2],
							})}
						>
							<Icon variant="close-v3" size="md" />
						</ActionIcon>
						<ActionIcon
							data-testid={VALUATION_ELEMENT_ID.FX_EXCHANGE_RATE_AT_VALUATION_SAVE}
							variant="filled"
							size="sm"
							disabled={isInvalid}
							onClick={onSave}
							sx={(theme) => ({
								color: theme.colors.white[0],
								backgroundColor: theme.colors.blue[6],
							})}
						>
							<Icon variant="tick" size="md" />
						</ActionIcon>
					</>
				)}
			</RateActions>
		</Container>
	);
};
const _EditableRateSectionExport = memo(EditableRateSection);

export { _EditableRateSectionExport as EditableRateSection };
